import React from 'react';
import { formatContractPaymentSchedule } from '../../utils/contractPaymentSchedule';
import HandwrittenSignaturePad from './HandwrittenSignaturePad';
import MobileSignaturePad from '../mobile/MobileSignaturePad';

export type ContractTemplateSummary = {
  number?: string;
  date?: string;
  location?: string;
  ownerName?: string;
  ownerId?: string;
  ownerAddress?: string;
  ownerPhone?: string;
  tenantName?: string;
  tenantId?: string;
  tenantAddress?: string;
  tenantPhone?: string;
  propertyAddress?: string;
  unitName?: string;
  propertyType?: string;
  area?: string;
  startDate?: string;
  endDate?: string;
  rent?: string;
  deposit?: string;
  rentPaymentDay?: string | number;
  paymentSchedule?: string;
  contractEffectiveDate?: string;
};

type Props = {
  summary: ContractTemplateSummary;
  landlordSignature?: string | null;
  tenantSignature?: string | null;
  canSignLandlord?: boolean;
  canSignTenant?: boolean;
  signingRole?: 'landlord' | 'tenant' | null;
  onSaveSignature?: (role: 'landlord' | 'tenant', image: string) => Promise<void> | void;
  signInPopup?: boolean;
};

const Field: React.FC<{ label: string; value?: string | number | null; wide?: boolean }> = ({
  label,
  value,
  wide,
}) => (
  <p className={wide ? 'md:col-span-2' : ''}>
    <span className="font-semibold">{label}:</span> {value || '—'}
  </p>
);

const SignatureBox: React.FC<{ label: string; name?: string; image?: string | null }> = ({
  label,
  name,
  image,
}) => (
  <div className="border-t border-[#e7dcc3] pt-4">
    <p className="mb-1 font-bold uppercase text-[#1f2937]">{label}</p>
    <p className="mb-3 text-sm text-gray-600">{image ? 'Đã ký' : 'Chưa ký'}</p>
    <div className="rounded-xl border border-[#dcc79d] bg-white px-3 py-2">
      {image ? (
        <img src={image} alt={`Chữ ký ${label}`} className="mx-auto h-28 w-full object-contain" />
      ) : (
        <div className="flex h-28 items-center justify-center text-sm text-gray-400">Chưa có chữ ký</div>
      )}
    </div>
    <p className="mt-3 font-semibold text-gray-800">{name || '—'}</p>
  </div>
);

const ContractRentalDocumentBody: React.FC<Props> = ({
  summary,
  landlordSignature,
  tenantSignature,
  canSignLandlord = false,
  canSignTenant = false,
  signingRole = null,
  onSaveSignature,
  signInPopup = false,
}) => {
  const paymentSchedule = formatContractPaymentSchedule(
    summary.rentPaymentDay,
    summary.paymentSchedule,
  );

  return (
    <div className="contract-template-content rounded-[24px] border border-[#dcc79d] bg-white p-4 shadow-[0_20px_50px_rgba(86,58,20,0.08)] sm:p-6">
      <div className="mb-8 border-b border-[#e7dcc3] pb-4 text-center">
        <div className="mb-2 text-base font-bold uppercase tracking-[0.2em] text-[#1f2937] sm:text-xl">
          CỘNG HÒA XÃ HỘI CHỦ NGHĨA VIỆT NAM
        </div>
        <div className="text-sm font-medium text-[#1f2937] sm:text-base">Độc lập - Tự do - Hạnh phúc</div>
        <div className="mt-6 text-xl font-extrabold uppercase tracking-wide text-[#8c5226] sm:text-2xl">
          HỢP ĐỒNG THUÊ NHÀ Ở
        </div>
        <div className="mt-2 text-sm font-medium text-gray-700">Số: {summary.number || '—'}</div>
      </div>

      <p className="mb-6 text-base leading-7 text-gray-800">
        Hôm nay, ngày <span className="font-semibold">{summary.date || '—'}</span>, tại{' '}
        <span className="font-semibold">{summary.location || '—'}</span>, chúng tôi gồm:
      </p>

      <section className="mb-8 space-y-4">
        <h2 className="text-lg font-bold uppercase text-[#8c5226]">I. BÊN CHO THUÊ</h2>
        <div className="grid gap-2 text-[15px] leading-7 text-gray-800 md:grid-cols-2">
          <Field label="Họ và tên" value={summary.ownerName} />
          <Field label="CCCD" value={summary.ownerId} />
          <Field label="Số điện thoại" value={summary.ownerPhone} />
          <Field label="Địa chỉ" value={summary.ownerAddress} wide />
        </div>
        <p className="text-gray-800">
          Sau đây gọi là <span className="font-semibold">Bên cho thuê</span>.
        </p>
      </section>

      <section className="mb-8 space-y-4">
        <h2 className="text-lg font-bold uppercase text-[#8c5226]">II. BÊN THUÊ</h2>
        <div className="grid gap-2 text-[15px] leading-7 text-gray-800 md:grid-cols-2">
          <Field label="Họ và tên" value={summary.tenantName} />
          <Field label="CCCD" value={summary.tenantId} />
          <Field label="Số điện thoại" value={summary.tenantPhone} />
          <Field label="Địa chỉ" value={summary.tenantAddress} wide />
        </div>
        <p className="text-gray-800">
          Sau đây gọi là <span className="font-semibold">Bên thuê</span>.
        </p>
        <p className="text-gray-800">Hai bên tự nguyện thỏa thuận ký kết Hợp đồng thuê nhà ở với các điều khoản sau:</p>
      </section>

      <section className="mb-8 space-y-3">
        <h3 className="text-lg font-bold uppercase text-[#8c5226]">Điều 1. Nhà ở cho thuê</h3>
        <p className="leading-7 text-gray-800">Bên cho thuê đồng ý cho Bên thuê thuê nhà/phòng tại:</p>
        <div className="space-y-2 text-[15px] leading-7 text-gray-800">
          <Field label="Địa chỉ" value={summary.propertyAddress} />
          <Field label="Phòng/căn số" value={summary.unitName} />
          <Field label="Loại nhà ở" value={summary.propertyType} />
          <Field label="Diện tích sử dụng" value={summary.area ? `${summary.area} m²` : ''} />
        </div>
        <p className="leading-7 text-gray-800">
          Bên cho thuê cam kết có quyền hợp pháp đối với nhà ở và có quyền cho thuê nhà ở theo quy định pháp luật.
        </p>
      </section>

      <section className="mb-8 space-y-3">
        <h3 className="text-lg font-bold uppercase text-[#8c5226]">Điều 2. Thời hạn thuê</h3>
        <p className="leading-7 text-gray-800">
          Thời hạn thuê từ ngày <span className="font-semibold">{summary.startDate || '—'}</span> đến hết ngày{' '}
          <span className="font-semibold">{summary.endDate || '—'}</span>.
        </p>
        <p className="leading-7 text-gray-800">Việc tiếp tục thuê sau khi hết thời hạn do hai bên thỏa thuận.</p>
      </section>

      <section className="mb-8 space-y-3">
        <h3 className="text-lg font-bold uppercase text-[#8c5226]">Điều 3. Giá thuê, tiền đặt cọc và các chi phí sử dụng</h3>
        <div className="space-y-2 text-[15px] leading-7 text-gray-800">
          <p>
            <span className="font-semibold">1. Giá thuê:</span> {summary.rent || '—'} đồng/tháng.
          </p>
          <p>
            <span className="font-semibold">2. Tiền đặt cọc:</span> {summary.deposit || '—'} đồng.
          </p>
          <p>
            <span className="font-semibold">3. Phương thức và kỳ hạn thanh toán:</span> {paymentSchedule}.
          </p>
        </div>
        <p className="leading-7 text-gray-800">
          Chi phí điện, nước, internet và các dịch vụ khác phát sinh trong quá trình thuê do hai bên thỏa thuận về cách tính, đơn giá và phương thức thanh toán.
        </p>
      </section>

      <section className="mb-8 space-y-3">
        <h3 className="text-lg font-bold uppercase text-[#8c5226]">Điều 4. Quyền và nghĩa vụ của Bên cho thuê</h3>
        <p className="leading-7 text-gray-800 font-semibold">Bên cho thuê có quyền:</p>
        <ul className="list-decimal space-y-2 pl-6 text-[15px] leading-7 text-gray-800">
          <li>Nhận tiền thuê và các khoản thanh toán khác theo thỏa thuận.</li>
          <li>Yêu cầu Bên thuê sử dụng nhà ở đúng mục đích và giữ gìn tài sản.</li>
          <li>Nhận lại nhà khi Hợp đồng hết hạn hoặc chấm dứt hợp pháp.</li>
          <li>Yêu cầu Bên thuê bồi thường thiệt hại do lỗi của Bên thuê gây ra theo quy định pháp luật.</li>
        </ul>
        <p className="leading-7 text-gray-800 font-semibold">Bên cho thuê có nghĩa vụ:</p>
        <ul className="list-decimal space-y-2 pl-6 text-[15px] leading-7 text-gray-800">
          <li>Bàn giao nhà cho Bên thuê đúng thời hạn và theo thỏa thuận.</li>
          <li>Bảo đảm cho Bên thuê được sử dụng nhà ổn định trong thời hạn thuê, trừ trường hợp Hợp đồng chấm dứt theo thỏa thuận hoặc quy định pháp luật.</li>
          <li>Thực hiện các nghĩa vụ khác theo Hợp đồng và quy định pháp luật.</li>
        </ul>
      </section>

      <section className="mb-8 space-y-3">
        <h3 className="text-lg font-bold uppercase text-[#8c5226]">Điều 5. Quyền và nghĩa vụ của Bên thuê</h3>
        <p className="leading-7 text-gray-800 font-semibold">Bên thuê có quyền:</p>
        <ul className="list-decimal space-y-2 pl-6 text-[15px] leading-7 text-gray-800">
          <li>Nhận và sử dụng nhà ở theo đúng thỏa thuận.</li>
          <li>Được sử dụng nhà ở ổn định trong thời hạn thuê.</li>
          <li>Yêu cầu Bên cho thuê thực hiện các nghĩa vụ đã thỏa thuận.</li>
        </ul>
        <p className="leading-7 text-gray-800 font-semibold">Bên thuê có nghĩa vụ:</p>
        <ul className="list-decimal space-y-2 pl-6 text-[15px] leading-7 text-gray-800">
          <li>Thanh toán tiền thuê đầy đủ và đúng hạn.</li>
          <li>Sử dụng nhà ở đúng mục đích.</li>
          <li>Giữ gìn nhà ở và tài sản được bàn giao.</li>
          <li>Không tự ý sửa chữa, cải tạo hoặc thay đổi kết cấu nhà ở nếu chưa được Bên cho thuê đồng ý.</li>
          <li>Không cho thuê lại hoặc chuyển giao quyền sử dụng nhà ở cho người khác nếu chưa được Bên cho thuê đồng ý.</li>
          <li>Bàn giao lại nhà khi Hợp đồng hết hạn hoặc chấm dứt theo thỏa thuận.</li>
        </ul>
      </section>

      <section className="mb-8 space-y-3">
        <h3 className="text-lg font-bold uppercase text-[#8c5226]">Điều 6. Chấm dứt Hợp đồng</h3>
        <p className="leading-7 text-gray-800">Hợp đồng chấm dứt trong các trường hợp:</p>
        <ol className="list-decimal space-y-2 pl-6 text-[15px] leading-7 text-gray-800">
          <li>Hết thời hạn thuê.</li>
          <li>Hai bên thỏa thuận chấm dứt Hợp đồng.</li>
          <li>Một bên đơn phương chấm dứt Hợp đồng theo quy định pháp luật hoặc theo thỏa thuận của hai bên.</li>
          <li>Các trường hợp khác theo quy định pháp luật.</li>
        </ol>
        <p className="leading-7 text-gray-800">
          Khi Hợp đồng chấm dứt, hai bên có trách nhiệm hoàn thành các nghĩa vụ còn lại và thực hiện việc bàn giao nhà.
        </p>
        <p className="leading-7 text-gray-800">
          Việc hoàn trả tiền đặt cọc, nếu có, được thực hiện sau khi hai bên hoàn tất việc bàn giao và đối chiếu các nghĩa vụ thanh toán.
        </p>
      </section>

      <section className="mb-8 space-y-3">
        <h3 className="text-lg font-bold uppercase text-[#8c5226]">Điều 7. Cam kết của các bên</h3>
        <ol className="list-decimal space-y-2 pl-6 text-[15px] leading-7 text-gray-800">
          <li>Các thông tin cung cấp trong Hợp đồng là đúng sự thật.</li>
          <li>Hai bên tự nguyện ký kết Hợp đồng, không bị lừa dối hoặc ép buộc.</li>
          <li>Hai bên đã đọc, hiểu và đồng ý với toàn bộ nội dung Hợp đồng.</li>
          <li>Hai bên cam kết thực hiện đúng các thỏa thuận trong Hợp đồng và quy định của pháp luật.</li>
        </ol>
      </section>

      <section className="mb-8 space-y-3">
        <h3 className="text-lg font-bold uppercase text-[#8c5226]">Điều 8. Hiệu lực của Hợp đồng</h3>
        <ol className="list-decimal space-y-2 pl-6 text-[15px] leading-7 text-gray-800">
          <li>
            Hợp đồng có hiệu lực kể từ ngày{' '}
            <span className="font-semibold">{summary.contractEffectiveDate || '—'}</span>.
          </li>
          <li>
            Hợp đồng được lập thành <span className="font-semibold">02 bản</span> có giá trị pháp lý như nhau, mỗi bên giữ 01 bản.
          </li>
          <li>Việc sửa đổi, bổ sung Hợp đồng được thực hiện theo thỏa thuận của hai bên và được lập thành văn bản khi cần thiết.</li>
          <li>Những vấn đề chưa được quy định trong Hợp đồng này được thực hiện theo thỏa thuận của hai bên và quy định pháp luật có liên quan.</li>
        </ol>
      </section>

      <div className="grid gap-8 pt-6 md:grid-cols-2">
        {signInPopup ? (
          <MobileSignaturePad
            label="BÊN CHO THUÊ"
            signerName={summary.ownerName || '—'}
            savedImage={landlordSignature}
            canSign={canSignLandlord}
            busy={signingRole === 'landlord'}
            onSave={(image) => onSaveSignature?.('landlord', image)}
          />
        ) : canSignLandlord ? (
          <HandwrittenSignaturePad
            label="BÊN CHO THUÊ"
            signerName={summary.ownerName || '—'}
            savedImage={landlordSignature}
            canSign
            busy={signingRole === 'landlord'}
            onSave={(image) => onSaveSignature?.('landlord', image)}
          />
        ) : (
          <SignatureBox label="BÊN CHO THUÊ" name={summary.ownerName} image={landlordSignature} />
        )}
        {signInPopup ? (
          <MobileSignaturePad
            label="BÊN THUÊ"
            signerName={summary.tenantName || '—'}
            savedImage={tenantSignature}
            canSign={canSignTenant}
            busy={signingRole === 'tenant'}
            onSave={(image) => onSaveSignature?.('tenant', image)}
          />
        ) : canSignTenant ? (
          <HandwrittenSignaturePad
            label="BÊN THUÊ"
            signerName={summary.tenantName || '—'}
            savedImage={tenantSignature}
            canSign
            busy={signingRole === 'tenant'}
            onSave={(image) => onSaveSignature?.('tenant', image)}
          />
        ) : (
          <SignatureBox label="BÊN THUÊ" name={summary.tenantName} image={tenantSignature} />
        )}
      </div>
    </div>
  );
};

export default ContractRentalDocumentBody;
